define('presentation/settings/closeAccount/facades/closeAccountFacade',[
    'common/promises/promiseFactory',
    'externalDependencies/clientWebSocket',
    'persistence/dataProviders/paymentMethodDataProvider',
    'persistence/webSocket/webSocketApp'
], function() {

    const PromiseFactoryConstructor = require('common/promises/promiseFactory');
    const _promiseFactory = new PromiseFactoryConstructor();

    const ClientWebSocketConstructor = require('externalDependencies/clientWebSocket');
    const _closeAccountWebSocket = ClientWebSocketConstructor.forApplication("closeAccount");
    const _accountPlanWebSocket = ClientWebSocketConstructor.forApplication("accountPlan");

    let _webSocketApp = null;
    let _paymentMethodDataProvider = null;

    let _featuresList = [];
    let _liveReceptionistIssueList = [];
    let _tooExpensiveFeedbackReasons = [];
    let _noLongerNeededFeedbackReasons = [];

    const _getFeedbackReasonDisplayNames = (selectedReasons, referenceList, selectedIdKey, displayNameKey) => {
        return selectedReasons
            .map((selectedReason) => {
                const match = referenceList.find((item) => item.id === selectedReason[selectedIdKey]);
                return match ? match[displayNameKey] : undefined;
            })
            .filter((displayName) => displayName !== undefined)
            .join(", ");
    };

    const _getOtherFeedback = (selectedReasons, otherFeedbackKey) => {
        return selectedReasons.find((selectedReason) => selectedReason[otherFeedbackKey]);
    };

    const _formatCloseAccountFeedbackForNotation = (feedback) => {
        const lacksFeaturesFeedbackReasons = feedback.lacksFeaturesFeedbackReasons || [];
        const lacksFeaturesReasons = _getFeedbackReasonDisplayNames(
            lacksFeaturesFeedbackReasons, _featuresList, 'lacksFeaturesReasonId', 'displayName'
        );
        const lacksFeaturesOtherReason = _getOtherFeedback(lacksFeaturesFeedbackReasons, 'lacksFeaturesOtherFeedback');

        const liveReceptionistIssueFeedbackReasons = feedback.liveReceptionistIssueFeedbackReasons || [];
        const liveReceptionistIssueReasons = _getFeedbackReasonDisplayNames(
            liveReceptionistIssueFeedbackReasons, _liveReceptionistIssueList, 'liveReceptionistIssueReasonId', 'displayName'
        );
        const liveReceptionistIssueOtherReason = _getOtherFeedback(liveReceptionistIssueFeedbackReasons, 'liveReceptionistIssueOtherFeedback');

        const tooExpensiveReason = _tooExpensiveFeedbackReasons.find((reason) => {
            return reason.id === feedback.accountClosedTooExpensiveFeedbackTypeDiscriminator;
        });

        const noLongerNeededReason = _noLongerNeededFeedbackReasons.find((reason) => {
            return reason.id === feedback.accountClosedNoLongerNeededFeedbackTypeDiscriminator;
        });

        return {
            lacksFeaturesReasons: lacksFeaturesReasons,
            lacksFeaturesOtherFeedback: lacksFeaturesOtherReason ? lacksFeaturesOtherReason.lacksFeaturesOtherFeedback : "",
            liveReceptionistIssueReasons: liveReceptionistIssueReasons,
            liveReceptionistIssueOtherFeedback: liveReceptionistIssueOtherReason ? liveReceptionistIssueOtherReason.liveReceptionistIssueOtherFeedback : null,
            noLongerNeededReason: noLongerNeededReason ? noLongerNeededReason.description : "",
            noLongerNeededOtherFeedback: feedback.noLongerNeededOtherFeedback,
            tooExpensiveReason: tooExpensiveReason ? tooExpensiveReason.description : "",
            tooExpensiveOtherFeedback: feedback.tooExpensiveOtherFeedback,
        };
    };

    const _closeAccount = (feedback, cancellationComment) => {
        return _promiseFactory.deferIndefinitely((deferredObject) => {
            const formattedFeedback = _formatCloseAccountFeedbackForNotation(feedback);
            const request = {
                accountClosedFeedbackTypeDiscriminator: feedback.accountClosedFeedbackTypeDiscriminator,
                fraudStatus: feedback.fraudStatus,
                reasonForCancellation: feedback.reasonForCancellation,
                cancellationComment: cancellationComment,
                lacksFeaturesReasons: formattedFeedback.lacksFeaturesReasons,
                lacksFeaturesOtherFeedback: formattedFeedback.lacksFeaturesOtherFeedback,
                liveReceptionistIssueReasons: formattedFeedback.liveReceptionistIssueReasons,
                liveReceptionistIssueOtherFeedback: formattedFeedback.liveReceptionistIssueOtherFeedback,
                noLongerNeededReason: formattedFeedback.noLongerNeededReason,
                noLongerNeededOtherFeedback: formattedFeedback.noLongerNeededOtherFeedback,
                tooExpensiveReason: formattedFeedback.tooExpensiveReason,
                tooExpensiveOtherFeedback: formattedFeedback.tooExpensiveOtherFeedback
            };
            _webSocketApp.send("close_account", request, (result) => {
                if (result) {
                    _saveCloseAccountFeedback(feedback)
                        .fail(deferredObject.reject)
                        .done(deferredObject.resolve);
                } else {
                    deferredObject.reject();
                }
            });
        });
    };

    const _getCloseAccountReasons = () => {
        return _promiseFactory.defer((deferredObject) => {
            _closeAccountWebSocket.send("getCloseAccountFeedbackReasons", {}, (reasons) => {
                const { closeAccountFeedbackReasons, tooExpensiveFeedbackReasons, noLongerNeededFeedbackReasons, liveReceptionistIssueList, featuresList } = reasons;
                _tooExpensiveFeedbackReasons = tooExpensiveFeedbackReasons.slice();
                _noLongerNeededFeedbackReasons = noLongerNeededFeedbackReasons.slice();
                _liveReceptionistIssueList = liveReceptionistIssueList.slice();
                _featuresList = featuresList.slice();
                deferredObject.resolve(closeAccountFeedbackReasons, tooExpensiveFeedbackReasons, noLongerNeededFeedbackReasons, liveReceptionistIssueList, featuresList);
            });
        });
    };

    const _getAccountPlan = () => {
        return _promiseFactory.defer((deferredObject) => {
            _accountPlanWebSocket.send("getAccountPlan", {}, (response) => {
                deferredObject.resolve(response);
            });
        });
    };

    const _getBillingMethod = () => {
        return _promiseFactory.defer((deferredObject) => {
            _paymentMethodDataProvider.getBillingMethod()
                .fail(deferredObject.reject)
                .done((result) => {
                    const { billingMethod } = result;
                    deferredObject.resolve(billingMethod);
                });
        });
    };

    const _isAccountInFreeTrial = () => {
        return _promiseFactory.defer((deferredObject) => {
            _paymentMethodDataProvider.getFreeTrialInfo()
                .fail(deferredObject.reject)
                .done((result) => {
                    const { isInFreeTrial } = result;
                    deferredObject.resolve(isInFreeTrial);
                });
        });
    };

    const _saveCloseAccountFeedback = (feedback) => {
        return _promiseFactory.defer((deferredObject) => {
            _closeAccountWebSocket.send("createAccountClosedFeedback", feedback, () => {
                deferredObject.resolve();
            });
        });
    };

    const _init = () => {
        const WebSocketAppConstructor = require('persistence/webSocket/webSocketApp');
        _webSocketApp = new WebSocketAppConstructor();
        _webSocketApp.init("account_profile");

        const PaymentMethodDataProviderConstructor = require('persistence/dataProviders/paymentMethodDataProvider');
        _paymentMethodDataProvider = new PaymentMethodDataProviderConstructor();
        _paymentMethodDataProvider.init();
    };

    return function() {
        const self = this;

        self.init = _init;
        self.closeAccount = _closeAccount;
        self.getAccountPlan = _getAccountPlan;
        self.getBillingMethod = _getBillingMethod;
        self.getCloseAccountReasons = _getCloseAccountReasons;
        self.isAccountInFreeTrial = _isAccountInFreeTrial;
    };
});

